/**
 * Portal — monta su contenido en `document.body`, fuera del árbol del DOM
 * donde la pantalla lo declara.
 *
 * 🔴 Existe por un defecto real, reportado en el panel de plataforma: el modal
 * aparecía **debajo** de otros componentes. La causa no es el `z-index` y por
 * eso subirlo no lo arregla — un elemento con `backdrop-filter` se convierte en
 * **bloque contenedor** de sus descendientes `position: fixed` **y** crea un
 * **contexto de apilamiento** propio. Desde adentro de ese contexto, ningún
 * `z-index` alcanza lo que está afuera: el modal queda encerrado. Lo mismo hace
 * cualquier ancestro con `transform`, `filter`, `perspective` o `contain`.
 *
 * El producto usa vidrio (`bg-glass`, que lleva `backdrop-filter`) en 239
 * paneles, así que el problema no es de una pantalla: es de cualquier modal
 * escrito a mano que quede dentro de uno.
 *
 * El `Modal` del design system ya monta por portal. Este componente es para los
 * **93 modales escritos a mano** que todavía no se migraron: envolverlos cierra
 * el defecto de apilamiento sin tocarles la estructura.
 *
 * ⚠️ Lo que el portal NO les da, y por eso no reemplaza la migración:
 * `role="dialog"`, `aria-modal`, la trampa de foco, el bloqueo del scroll de
 * fondo y la restauración del foco al cerrar. Eso sigue siendo del `Modal`.
 *
 * 🟢 Los eventos de React **siguen subiendo por el árbol de React**, no por el
 * del DOM, así que un `onClick` en un ancestro del JSX sigue recibiendo los
 * clics de adentro del portal. Lo que sí cambia es el DOM: un control que
 * estaba dentro de un `<form>` deja de pertenecerle, así que un modal con
 * campos que dependa del submit de un form externo **no se envuelve**.
 */
import { createPortal } from 'react-dom'
import type { ReactNode } from 'react'

export function Portal({ children }: { children: ReactNode }) {
  // ⚠️ `document.body` y no un nodo propio: crear uno exigiría montarlo en un
  // efecto, y entonces el primer render devolvería `null` — un modal que
  // aparece un cuadro tarde parpadea al abrirse.
  return createPortal(children, document.body)
}
